import { isEqual } from 'lodash'
import { SearchTradingPartnerHistoryPayload } from 'services/kraken-core/trading_partner/tradingPartner.service'

export type TranslationSettingsDeprecatedHistoryByDirection = Array<{
  inbound_translation_settings?: any
  outbound_translation_settings?: any
  history_id: number
  history_user?: string
  history_date: string
  history_change_reason?: string | null
}>

export const getTranslationSettingsDeprecatedHistoryByDirection = (
  direction: 'inbound' | 'outbound',
  tradingPartnerHistory: SearchTradingPartnerHistoryPayload[]
): TranslationSettingsDeprecatedHistoryByDirection => {
  const mappedByDirection = mapTranslationSettingsHistoryByDirection(
    direction,
    tradingPartnerHistory
  )
  const filteredByDiffs = filterTranslationSettingsHistoryByDiffs(direction, mappedByDirection)
  return filteredByDiffs
}

/**
 * Maps Trading Partner history entries to a new array of objects with
 * the translation settings for a specific direction (inbound or outbound)
 */
const mapTranslationSettingsHistoryByDirection = (
  direction: 'inbound' | 'outbound',
  tradingPartnerHistory: SearchTradingPartnerHistoryPayload[]
): TranslationSettingsDeprecatedHistoryByDirection => {
  return tradingPartnerHistory?.map(history => ({
    [direction + '_translation_settings']: history.translation_settings?.[direction] || {},
    history_id: history.history_id,
    history_user: history.history_user || 'Unknown User',
    history_date: history.history_date,
    history_change_reason: history.history_change_reason,
  }))
}

/**
 * Filters history entries to keep only those where translation settings changed
 *
 * Removes consecutive duplicate entries, preserving only the first entry and entries
 * where the translation settings differ from the previous kept entry
 *
 * @returns Filtered array containing only entries with changed translation settings
 */
const filterTranslationSettingsHistoryByDiffs = (
  direction: 'inbound' | 'outbound',
  tradingPartnerHistory: TranslationSettingsDeprecatedHistoryByDirection
): TranslationSettingsDeprecatedHistoryByDirection => {
  /** Sort by history_id ascending (oldest first) */
  const sortedHistory = [...tradingPartnerHistory].sort((a, b) => a.history_id - b.history_id)

  return (
    sortedHistory
      .reduce((acc, currentEntry, index) => {
        const isFirst = index === 0
        const lastKept = acc[acc.length - 1]

        const currentSettings = currentEntry[direction + '_translation_settings']
        const lastKeptSettings = lastKept?.[direction + '_translation_settings']

        const isDifferent = !lastKept || !isEqual(currentSettings, lastKeptSettings)
        const shouldKeep = isFirst || isDifferent

        if (shouldKeep) {
          acc.push(currentEntry)
        }

        return acc
      }, [])
      /** Reverse the array to get the latest changes first */
      .reverse()
  )
}
